import { get, ref, runTransaction, update } from "firebase/database";
import {
  ACTIVITY_MODE_WALK,
  normalizeActivityMode,
} from "./activityMode.js";

const toDecimalNumber = (value, fallback = 0) => {
  if (typeof value === "string") {
    const normalized = value.trim().replace(",", ".");
    const n = Number(normalized);
    return Number.isFinite(n) ? n : fallback;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toDistanceKm = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Number(n.toFixed(2));
};

export function monthKeyFromTs(ts = Date.now()) {
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function previousMonthKeyFromTs(ts = Date.now()) {
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return "";
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return monthKeyFromTs(d.getTime());
}

export function buildMonthlyRankingRows({
  monthKey,
  usersPublic = {},
  gamificationUsers = {},
  publicSnapshotRows = {},
  activityMode = ACTIVITY_MODE_WALK,
}) {
  const rowsByUid = {};
  const mode = normalizeActivityMode(activityMode, ACTIVITY_MODE_WALK);

  const resolveModeMonthStats = (source) => {
    const byMode = source?.monthlyStatsByMode?.[mode]?.[monthKey];
    if (byMode && typeof byMode === "object") return byMode;
    if (mode === ACTIVITY_MODE_WALK) {
      return source?.monthlyStats?.[monthKey] || {};
    }
    return {};
  };

  const upsert = (row) => {
    const uid = String(row?.uid || "").trim();
    if (!uid) return;
    const distanceKm = Number(
      Math.max(0, toDecimalNumber(row?.distanceKm, 0)).toFixed(2)
    );
    if (distanceKm <= 0) return;
    const updatedAt = Math.max(0, toDecimalNumber(row?.updatedAt, 0));
    const existing = rowsByUid[uid];
    const shouldReplace =
      !existing ||
      distanceKm > existing.distanceKm ||
      (distanceKm === existing.distanceKm && updatedAt > (existing.updatedAt || 0));
    if (!shouldReplace) return;
    rowsByUid[uid] = {
      uid,
      name: String(row?.name || existing?.name || uid).trim() || uid,
      distanceKm,
      updatedAt,
    };
  };

  Object.values(publicSnapshotRows || {}).forEach((row) => {
    if (!row?.activityMode && mode !== ACTIVITY_MODE_WALK) return;
    if (
      row?.activityMode &&
      normalizeActivityMode(row.activityMode, ACTIVITY_MODE_WALK) !== mode
    ) {
      return;
    }
    upsert({
      uid: row?.uid,
      name: row?.name || row?.uid || "Utilizador",
      distanceKm: row?.distanceKm,
      updatedAt: row?.updatedAt,
    });
  });

  Object.entries(usersPublic || {}).forEach(([uid, profile]) => {
    const monthStats = resolveModeMonthStats(profile?.gamification || {});
    const distanceKm = toDistanceKm(monthStats?.distanceKm);
    if (distanceKm <= 0) return;
    upsert({
      uid,
      name: profile?.name || profile?.username || uid,
      distanceKm,
      updatedAt: monthStats?.updatedAt || profile?.updatedAt || 0,
    });
  });

  Object.entries(gamificationUsers || {}).forEach(([uid, data]) => {
    const monthStats = resolveModeMonthStats(data || {});
    const distanceKm = toDistanceKm(monthStats?.distanceKm);
    if (distanceKm <= 0) return;
    const profile = usersPublic?.[uid] || {};
    upsert({
      uid,
      name:
        profile?.name ||
        profile?.username ||
        data?.name ||
        data?.displayName ||
        uid,
      distanceKm,
      updatedAt: Number(monthStats?.updatedAt || 0) || 0,
    });
  });

  return Object.values(rowsByUid)
    .sort((a, b) => {
      if (b.distanceKm !== a.distanceKm) return b.distanceKm - a.distanceKm;
      return (b.updatedAt || 0) - (a.updatedAt || 0);
    })
    .map((row, idx) => ({
      ...row,
      rank: idx + 1,
    }));
}

export async function awardMonthlyRankingXp({
  db,
  monthKey,
  rows,
  rankXpList,
  actorUid,
  actorLabel = "system",
  publicRowsLimit = 100,
  activityMode = ACTIVITY_MODE_WALK,
}) {
  const safeMonthKey = String(monthKey || "").trim();
  if (!safeMonthKey) return { ok: false, reason: "invalid_month" };
  const modeKey = normalizeActivityMode(activityMode, ACTIVITY_MODE_WALK);
  const rankingKey =
    modeKey === ACTIVITY_MODE_WALK ? safeMonthKey : `${safeMonthKey}-${modeKey}`;

  const topRows = Array.isArray(rows) ? rows.slice(0, 3) : [];
  if (!topRows.length) return { ok: false, reason: "no_rows" };

  const safeRankXpList = Array.isArray(rankXpList)
    ? rankXpList.map((xp) => Math.max(0, Math.floor(Number(xp || 0))))
    : [0, 0, 0];
  if (safeRankXpList.every((xp) => xp <= 0)) {
    return { ok: false, reason: "no_xp_configured" };
  }

  const now = Date.now();
  const actor = actorUid || actorLabel || "system";
  const lockRef = ref(db, `gamification/monthlyRankingAwards/${rankingKey}`);
  const persistRankingMetadata = async (winners, mode = "auto", extra = {}) => {
    const publicRanking = {};
    (Array.isArray(rows) ? rows : []).slice(0, publicRowsLimit).forEach((row, idx) => {
      publicRanking[row.uid] = {
        rank: idx + 1,
        uid: row.uid,
        name: row.name,
        distanceKm: row.distanceKm,
        activityMode: modeKey,
        updatedAt: row.updatedAt || now,
      };
    });

    const [rank1Xp, rank2Xp, rank3Xp] = safeRankXpList;

    await update(lockRef, {
      monthKey: safeMonthKey,
      activityMode: modeKey,
      status: "done",
      processedAt: Number(extra.processedAt || now),
      processedBy: extra.processedBy || actor,
      winners,
      rankXp: { rank1Xp, rank2Xp, rank3Xp },
      mode,
      ...extra,
    });

    const configUpdate = {
      monthlyRanking: {
        rank1Xp,
        rank2Xp,
        rank3Xp,
        lastAwardedMonth: safeMonthKey,
        lastAwardedActivityMode: modeKey,
        lastAwardedAt: Number(extra.processedAt || now),
        lastAwardedBy: extra.processedBy || actor,
      },
      [`monthlyRankingPublicByMode/${modeKey}/${safeMonthKey}`]: {
        monthKey: safeMonthKey,
        activityMode: modeKey,
        generatedAt: now,
        rows: publicRanking,
      },
      updatedAt: now,
      updatedBy: actor,
    };

    if (modeKey === ACTIVITY_MODE_WALK) {
      configUpdate[`monthlyRankingPublic/${safeMonthKey}`] = {
        monthKey: safeMonthKey,
        generatedAt: now,
        rows: publicRanking,
      };
    }

    await update(ref(db, "gamification/config"), configUpdate);
  };

  const repairMissingAwards = async (mode = "repair") => {
    const winners = [];
    let repairedCount = 0;

    for (let i = 0; i < 3; i += 1) {
      const row = topRows[i];
      const xp = safeRankXpList[i] || 0;
      if (!row || xp <= 0) continue;

      const awardKey =
        modeKey === ACTIVITY_MODE_WALK
          ? `${safeMonthKey}-rank-${i + 1}`
          : `${safeMonthKey}-${modeKey}-rank-${i + 1}`;
      const awardRef = ref(db, `gamification/users/${row.uid}/monthlyAwards/${awardKey}`);
      const awardSnap = await get(awardRef);
      if (!awardSnap.exists()) {
        await runTransaction(
          ref(db, `gamification/users/${row.uid}/xpTotal`),
          (current) => Math.max(0, Math.floor(Number(current || 0))) + xp,
          { applyLocally: false }
        );

        await update(ref(db, `gamification/users/${row.uid}`), {
          [`monthlyAwards/${awardKey}`]: {
            monthKey: safeMonthKey,
            activityMode: modeKey,
            rank: i + 1,
            xp,
            distanceKm: row.distanceKm,
            awardedAt: now,
            awardedBy: actor,
            mode,
          },
          lastGainAt: now,
          updatedAt: now,
        });
        repairedCount += 1;
      }

      winners.push({
        uid: row.uid,
        name: row.name,
        rank: i + 1,
        xp,
        distanceKm: row.distanceKm,
      });
    }

    await persistRankingMetadata(winners, repairedCount > 0 ? mode : "auto", {
      repairedAt: repairedCount > 0 ? now : null,
      repairedBy: repairedCount > 0 ? actor : null,
      repairedCount,
    });

    return {
      ok: repairedCount > 0,
      reason: repairedCount > 0 ? "repaired" : "already_processed",
      winners,
      repairedCount,
    };
  };

  const lockRes = await runTransaction(
    lockRef,
    (current) => {
      const startedAt = Number(current?.startedAt || 0);
      const isFreshProcessing =
        current?.status === "processing" && now - startedAt < 15 * 60 * 1000;
      if (Number(current?.processedAt || 0) > 0) return;
      if (isFreshProcessing) return;
      return {
      monthKey: safeMonthKey,
      activityMode: modeKey,
      status: "processing",
        startedAt: now,
        startedBy: actor,
        mode: "auto",
      };
    },
    { applyLocally: false }
  );

  if (!lockRes.committed) {
    const lockSnap = await get(lockRef).catch(() => null);
    const lockData = lockSnap?.exists?.() ? lockSnap.val() || {} : {};
    if (Number(lockData?.processedAt || 0) > 0 || lockData?.status === "done") {
      return repairMissingAwards("repair");
    }
    return { ok: false, reason: "already_processed" };
  }

  try {
    const winners = [];
    for (let i = 0; i < 3; i += 1) {
      const row = topRows[i];
      const xp = safeRankXpList[i] || 0;
      if (!row || xp <= 0) continue;

      await runTransaction(
        ref(db, `gamification/users/${row.uid}/xpTotal`),
        (current) => Math.max(0, Math.floor(Number(current || 0))) + xp,
        { applyLocally: false }
      );

      const awardKey =
        modeKey === ACTIVITY_MODE_WALK
          ? `${safeMonthKey}-rank-${i + 1}`
          : `${safeMonthKey}-${modeKey}-rank-${i + 1}`;
      await update(ref(db, `gamification/users/${row.uid}`), {
        [`monthlyAwards/${awardKey}`]: {
          monthKey: safeMonthKey,
          activityMode: modeKey,
          rank: i + 1,
          xp,
          distanceKm: row.distanceKm,
          awardedAt: now,
          awardedBy: actor,
          mode: "auto",
        },
        lastGainAt: now,
        updatedAt: now,
      });

      winners.push({
        uid: row.uid,
        name: row.name,
        rank: i + 1,
        xp,
        distanceKm: row.distanceKm,
      });
    }

    await persistRankingMetadata(winners, "auto");

    return { ok: true, reason: "awarded", winners };
  } catch (err) {
    await update(lockRef, {
      monthKey: safeMonthKey,
      status: "error",
      failedAt: Date.now(),
      failedBy: actor,
      errorMessage: String(err?.message || err || "unknown_error"),
      mode: "auto",
    }).catch(() => {});
    throw err;
  }
}

export async function autoAwardPreviousMonthlyRankingXp({
  db,
  config,
  usersPublic = {},
  gamificationUsers = {},
  nowTs = Date.now(),
  actorUid,
  actorLabel = "system",
  activityMode = ACTIVITY_MODE_WALK,
}) {
  let safeConfig = config && typeof config === "object" ? config : {};
  if (!Object.keys(safeConfig).length || !safeConfig?.monthlyRanking) {
    try {
      const snap = await get(ref(db, "gamification/config"));
      safeConfig = snap.val() || {};
    } catch {
      safeConfig = {};
    }
  }

  const previousMonthKey = previousMonthKeyFromTs(nowTs);
  if (!previousMonthKey) return { ok: false, reason: "invalid_previous_month" };
  const modeKey = normalizeActivityMode(activityMode, ACTIVITY_MODE_WALK);

  const rankXpList = [
    Math.max(0, Math.floor(Number(safeConfig?.monthlyRanking?.rank1Xp || 0))),
    Math.max(0, Math.floor(Number(safeConfig?.monthlyRanking?.rank2Xp || 0))),
    Math.max(0, Math.floor(Number(safeConfig?.monthlyRanking?.rank3Xp || 0))),
  ];

  let safeUsersPublic =
    usersPublic && typeof usersPublic === "object" ? usersPublic : {};
  let safeGamificationUsers =
    gamificationUsers && typeof gamificationUsers === "object" ? gamificationUsers : {};

  if (!Object.keys(safeUsersPublic).length) {
    try {
      const snap = await get(ref(db, "usersPublic"));
      safeUsersPublic = snap.val() || {};
    } catch {
      safeUsersPublic = {};
    }
  }

  if (!Object.keys(safeGamificationUsers).length) {
    try {
      const snap = await get(ref(db, "gamification/users"));
      safeGamificationUsers = snap.val() || {};
    } catch {
      safeGamificationUsers = {};
    }
  }

  const rows = buildMonthlyRankingRows({
    monthKey: previousMonthKey,
    usersPublic: safeUsersPublic,
    gamificationUsers: safeGamificationUsers,
    publicSnapshotRows:
      safeConfig?.monthlyRankingPublicByMode?.[modeKey]?.[previousMonthKey]
        ?.rows ||
      (modeKey === ACTIVITY_MODE_WALK
        ? safeConfig?.monthlyRankingPublic?.[previousMonthKey]?.rows
        : {}) ||
      {},
    activityMode: modeKey,
  });

  return awardMonthlyRankingXp({
    db,
    monthKey: previousMonthKey,
    rows,
    rankXpList,
    actorUid,
    actorLabel,
    activityMode: modeKey,
  });
}
